import React from 'react';
import { Form, Input, Radio, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../i18n/libelles';

/**
 * Numéro de procès-verbal - peut être illisible ou inconnu.
 *
 * Sur une partie des PV reçus, le numéro ne se lit pas (photocopie, tampon) ou n'a
 * pas été communiqué. Le champ étant obligatoire, l'agent ne pouvait pas enregistrer
 * le PV, ou inventait une valeur pour passer. Deux mentions en tiennent lieu :
 * « ILLISIBLE » et « INCONNU ». Ce ne sont pas des numéros : le backend les exempte
 * du contrôle d'unicité (voir `NumeroPv` côté serveur), plusieurs PV peuvent donc
 * les porter. Le vrai numéro se saisit plus tard en modifiant le PV.
 */
export const NUMERO_PV_ILLISIBLE = 'ILLISIBLE';
export const NUMERO_PV_INCONNU = 'INCONNU';

const ETATS = avecLibelleTraduit([
  { value: 'LISIBLE' },
  { value: NUMERO_PV_ILLISIBLE },
  { value: NUMERO_PV_INCONNU },
], 'pvSinistres:numeroPv.etats');

export const estNumeroPvNonRenseigne = (numero) => {
  const n = String(numero || '').trim().toUpperCase();
  return n === NUMERO_PV_ILLISIBLE || n === NUMERO_PV_INCONNU;
};

/** État à poser dans le formulaire quand on le pré-remplit avec un PV existant. */
export const etatDepuisNumeroPv = (numero) =>
  estNumeroPvNonRenseigne(numero) ? String(numero).trim().toUpperCase() : 'LISIBLE';

/**
 * Champ « Numéro du PV » : le choix Lisible / Illisible / Inconnu, puis la saisie.
 *
 * `etatNumeroPv` n'est qu'un état d'écran : les payloads sont construits champ par
 * champ et ne l'envoient pas. Le formulaire qui pré-remplit `numeroPv` doit aussi
 * poser `etatNumeroPv: etatDepuisNumeroPv(numero)`.
 */
export function ChampNumeroPv({ form, label, tooltip, placeholder }) {
  const { t } = useTranslation('pvSinistres');
  const etat = Form.useWatch('etatNumeroPv', form) || 'LISIBLE';
  const lisible = etat === 'LISIBLE';

  const changerEtat = (e) => {
    const suivant = e.target.value;
    if (suivant === 'LISIBLE') {
      // On repart d'un champ vide plutôt que de laisser la mention à corriger.
      if (estNumeroPvNonRenseigne(form.getFieldValue('numeroPv'))) {
        form.setFieldValue('numeroPv', undefined);
      }
    } else {
      form.setFieldValue('numeroPv', suivant);
    }
    form.validateFields(['numeroPv']).catch(() => { });
  };

  return (
    <Form.Item label={label === undefined ? t('numeroPv.label') : label} tooltip={tooltip} required style={{ marginBottom: 0 }}>
      <Form.Item name="etatNumeroPv" initialValue="LISIBLE" style={{ marginBottom: 8 }}>
        <Radio.Group size="small" optionType="button" options={ETATS} onChange={changerEtat} />
      </Form.Item>
      <Form.Item
        name="numeroPv"
        rules={lisible ? [{ required: true, whitespace: true, message: t('numeroPv.requis') }] : []}
      >
        {/* « N° » en addon, hors de la valeur (voir nettoyerNumeroPv) ; retiré devant une
            mention, qui n'est pas un numéro. */}
        <Input addonBefore={lisible ? t('numeroPv.prefixe') : undefined} placeholder={placeholder === undefined ? t('numeroPv.placeholder') : placeholder} maxLength={60} disabled={!lisible} />
      </Form.Item>
    </Form.Item>
  );
}

/** Affichage d'un numéro : une mention d'absence se lit comme telle, pas comme un numéro. */
export function NumeroPvAffiche({ numero }) {
  const { t } = useTranslation('pvSinistres');
  if (!estNumeroPvNonRenseigne(numero)) return numero || '-';
  const illisible = String(numero).trim().toUpperCase() === NUMERO_PV_ILLISIBLE;
  return (
    <Tag color="orange" style={{ borderRadius: 12, marginInlineEnd: 0 }}>
      {illisible ? t('numeroPv.affiche.illisible') : t('numeroPv.affiche.inconnu')}
    </Tag>
  );
}

/**
 * Le champ de saisie affiche « N° » en prefixe fixe, hors de la valeur. Ce
 * nettoyage couvre le cas restant : le prefixe tape ou colle malgre tout. Un PV
 * enregistre sous « N°123 » ne se retrouve plus en cherchant « 123 ».
 *
 * Le « N » n'est retire que s'il sert de prefixe de numerotation - signe numero,
 * ordinal, ou « No » suivi d'un separateur. Un numero qui commence reellement par
 * la lettre N (« N123 », « NORD-45 ») sort intact.
 */
const PREFIXE = /^\s*n\s*(?:[°º]\s*|o[\s.:-]+)/i;

export const nettoyerNumeroPv = (valeur) =>
  String(valeur ?? '').replace(PREFIXE, '').trim();
